import { useRef, useState, useEffect, useCallback } from 'react'
import { motion } from 'motion/react'
import FilterSelect from './FilterSelect'
import FilterSearch from '@/components/content/common/FilterSearch'
import { usePageContext } from '@/components/content/common/PageContext'
import { cn } from '@/lib/utils'
import { easeShort } from '@/lib/transitions'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { Category } from '@/payload-types'

type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug'>

type FiltersProps = {
  collection?: string
  filters: CategoryFilter[]
  handleSelected: (selection: CategoryFilter) => void
  sticky?: boolean
  initialised?: React.RefObject<boolean>
  picker?: React.ReactNode
  fold?: number
  overlay?: boolean
}

export default function Filters({
  collection,
  filters,
  handleSelected,
  sticky,
  picker,
  fold = 663,
  overlay = true,
}: FiltersProps) {
  const [fixed, setFixed] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)

  const handleSticky = useCallback(() => {
    setFixed(window.scrollY > fold)
  }, [fold])

  useEffect(() => {
    let ticking = false

    const throttledSticky = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleSticky()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledSticky, { passive: true })
    return () => window.removeEventListener('scroll', throttledSticky)
  }, [handleSticky])

  const filterContent = (
    <div className="mx-auto flex max-w-[1064px] flex-col items-center gap-4 p-4 xs:flex-row">
      {picker && <div className="hidden navigation:block">{picker}</div>}
      <div
        ref={scrollRef}
        className="flex w-full flex-1 items-center justify-between gap-3 xs:justify-start"
      >
        <DefaultToggle collection={collection} handleSelected={handleSelected} />
        <FilterSelect filters={filters} />
      </div>

      <FilterSearch placeholder={collection} overlay={overlay} />
    </div>
  )

  return (
    <div
      className={cn(
        'hide-scrollbar',
        sticky &&
          'navigation sticky top-20 z-30 w-full border-b border-black/0 bg-white/95 transition-opacity before:absolute before:inset-0 before:-z-10 before:backdrop-blur-md dark:bg-black/95',
        fixed && 'border-gray-100',
      )}
    >
      {filterContent}
    </div>
  )
}

export function DefaultToggle({
  collection,
  handleSelected,
}: {
  collection?: string
  handleSelected: (selection: CategoryFilter) => void
}) {
  const { md } = useBreakpoint()
  const { selectedFilter } = usePageContext()

  const pillClass =
    'relative flex items-center px-3 text-14 font-semibold text-gray-800 transition-colors cursor-pointer h-full'

  const items = [
    { id: 0, name: 'Featured', slug: 'featured' },
    { id: 0, name: collection ? `All ${collection}` : 'All', slug: 'all' },
  ]

  return (
    <div className="relative flex h-10 items-center gap-0.5 rounded-full border border-black/10 bg-gray-50 p-[3px]">
      {items.map((item) => (
        <div
          key={item.slug}
          className={cn(pillClass, selectedFilter?.slug === item.slug && 'text-white')}
          onClick={() => handleSelected(item)}
        >
          <span className="relative z-20 whitespace-nowrap capitalize">
            {!md ? (item.name.includes('All') ? 'All' : item.name) : item.name}
          </span>
          {selectedFilter?.slug === item.slug && (
            <motion.div
              className="absolute left-0 top-0 z-10 h-full w-full rounded-full border border-black/10 bg-primary-500"
              layoutId="selectedFilter"
              transition={easeShort}
            />
          )}
        </div>
      ))}
    </div>
  )
}
